/**
 * Public RFQ Targeting Criteria Hook
 * 
 * Manages vendor targeting criteria for public RFQs, enabling clients to define
 * specific requirements that vendors must meet to be invited. Also calculates
 * vendor match scores and estimates the number of eligible vendors.
 * 
 * **Targeting Criteria Types:**
 * - **Geographic**: Maximum distance from job location
 * - **Certifications**: Required certification types
 * - **Rating**: Minimum vendor rating threshold
 * - **Experience**: Minimum completed jobs
 * - **Verification**: Minimum verification level (basic/verified/premium)
 * 
 * **Match Score Calculation:**
 * - Each criterion contributes to a 0-100 score
 * - Vendors scoring ≥90 are considered "perfect matches"
 * - Score breakdown shows performance per criterion
 * - Real-time calculation as criteria are added
 * 
 * **Eligible Vendor Estimation:**
 * - Counts vendors meeting all criteria at ≥90% score
 * - Uses database RPC for efficient calculation
 * - Updates automatically when criteria change
 * - Helps clients gauge market availability
 * 
 * **Key Features:**
 * - Fetch existing criteria for an RFQ
 * - Calculate current user's match score (vendors only)
 * - Estimate perfect match count before publishing
 * - Save/update targeting criteria
 * - Clear all criteria
 * 
 * **Workflow:**
 * 1. Client defines targeting criteria
 * 2. System estimates eligible vendor count
 * 3. Vendor views RFQ and sees their match score
 * 4. System auto-invites vendors based on criteria
 * 
 * @param {string} rfqId - Optional RFQ ID to fetch criteria for
 * 
 * @returns {Object} RFQ criteria management
 * @returns {PublicRFQCriteria | null} criteria - Current targeting criteria
 * @returns {VendorMatchScore | null} matchScore - Vendor's match score (vendors only)
 * @returns {number} perfectMatchCount - Estimated eligible vendors
 * @returns {boolean} loading - Loading state
 * @returns {Function} fetchCriteria - Refresh criteria data
 * @returns {Function} fetchMyMatchScore - Recalculate vendor match score
 * @returns {Function} calculatePerfectMatches - Estimate eligible vendor count
 * @returns {Function} saveCriteria - Save/update criteria
 * @returns {Function} clearCriteria - Remove all criteria
 * 
 * @example
 * // Client sets targeting criteria
 * const {
 *   criteria,
 *   perfectMatchCount,
 *   calculatePerfectMatches,
 *   saveCriteria
 * } = usePublicRFQCriteria(rfqId);
 * 
 * // Define criteria
 * const newCriteria = {
 *   max_distance_miles: 50,
 *   min_rating: 4.0,
 *   min_completed_jobs: 10,
 *   required_certifications: [certId1, certId2]
 * };
 * 
 * // Calculate and save
 * const count = await calculatePerfectMatches(newCriteria);
 * console.log(`${count} eligible vendors`);
 * await saveCriteria(newCriteria);
 * 
 * @example
 * // Vendor views their match score
 * const { matchScore } = usePublicRFQCriteria(rfqId);
 * 
 * if (matchScore) {
 *   console.log(`Match score: ${matchScore.score}%`);
 *   console.log(`Distance met: ${matchScore.details.distance_met}`);
 *   console.log(`Certifications: ${matchScore.details.certifications_matched}`);
 * }
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { PublicRFQCriteria, VendorMatchScore } from '@/lib/types/job';

export const usePublicRFQCriteria = (rfqId?: string) => {
  const [criteria, setCriteria] = useState<PublicRFQCriteria | null>(null);
  const [matchScore, setMatchScore] = useState<VendorMatchScore | null>(null);
  const [perfectMatchCount, setPerfectMatchCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (rfqId) {
      fetchCriteria();
      fetchMyMatchScore();
    }
  }, [rfqId]);

  const fetchCriteria = async () => {
    if (!rfqId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rfq_targeting_criteria' as any)
        .select('*')
        .eq('rfq_id', rfqId)
        .maybeSingle() as any;

      if (error) throw error;
      if (data) {
        setCriteria(data as any as PublicRFQCriteria);
        setPerfectMatchCount((data as any).estimated_matching_vendors || 0);
      }
    } catch (error) {
      console.error('Error fetching public RFQ criteria:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyMatchScore = async () => {
    if (!rfqId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .rpc('calculate_vendor_match_score', {
          p_vendor_user_id: user.id,
          p_rfq_id: rfqId
        });

      if (error) {
        console.error('Error calculating match score:', error);
        return;
      }

      if (data) {
        setMatchScore(data as unknown as VendorMatchScore);
      }
    } catch (error) {
      console.error('Error fetching match score:', error);
    }
  };

  const calculatePerfectMatches = async (
    criteriaData: Partial<PublicRFQCriteria>
  ): Promise<number> => {
    if (!rfqId) return 0;

    try {
      // Save criteria first
      await supabase
        .from('rfq_targeting_criteria' as any)
        .upsert({
          rfq_id: rfqId,
          ...criteriaData,
        } as any);

      // Count vendors with perfect match
      const { data: countData, error: countError } = await supabase
        .rpc('count_eligible_vendors_for_public_rfq', {
          p_rfq_id: rfqId
        });

      if (countError) {
        console.error('Error counting eligible vendors:', countError);
        throw countError;
      }

      const count = typeof countData === 'number' ? countData : 0;
      setPerfectMatchCount(count);
      
      // Update the count in the criteria
      await supabase
        .from('rfq_targeting_criteria' as any)
        .update({ 
          estimated_matching_vendors: count,
          last_match_calculation_at: new Date().toISOString()
        } as any)
        .eq('rfq_id', rfqId);

      return count;
    } catch (error) {
      console.error('Error calculating perfect matches:', error);
      toast({
        title: 'Error',
        description: 'Failed to calculate vendor matches',
        variant: 'destructive',
      });
      return 0;
    }
  };

  const saveCriteria = async (
    criteriaData: Partial<PublicRFQCriteria>
  ): Promise<boolean> => {
    if (!rfqId) return false;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('rfq_targeting_criteria' as any)
        .upsert({
          rfq_id: rfqId,
          ...criteriaData,
        } as any);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Match criteria saved successfully',
      });

      await fetchCriteria();
      return true;
    } catch (error) {
      console.error('Error saving criteria:', error);
      toast({
        title: 'Error',
        description: 'Failed to save match criteria',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const clearCriteria = async (): Promise<boolean> => {
    if (!rfqId) return false;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('rfq_targeting_criteria' as any)
        .delete()
        .eq('rfq_id', rfqId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Match criteria removed',
      });

      setCriteria(null);
      setPerfectMatchCount(0);
      return true;
    } catch (error) {
      console.error('Error clearing criteria:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove criteria',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    criteria,
    matchScore,
    perfectMatchCount,
    loading,
    fetchCriteria,
    fetchMyMatchScore,
    calculatePerfectMatches,
    saveCriteria,
    clearCriteria,
  };
};
